// Technical Analysis Indicators for Trading Charts
import type { KlineData } from '../types/mexc';

export interface CandlestickData {
  timestamp: number;
  time: string;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
  amount: number;
}

export interface MovingAverageData {
  timestamp: number;
  sma20?: number;
  sma50?: number;
  ema20?: number;
  ema50?: number;
}

export interface RSIData {
  timestamp: number;
  rsi: number;
}

export interface SupportResistanceLevel {
  price: number;
  strength: number;
  type: 'support' | 'resistance';
  touches: number;
}

/**
 * Calculate Simple Moving Average (SMA)
 */
export function calculateSMA(data: number[], period: number): (number | null)[] {
  const result: (number | null)[] = [];
  
  for (let i = 0; i < data.length; i++) {
    if (i < period - 1) {
      result.push(null);
    } else {
      const sum = data.slice(i - period + 1, i + 1).reduce((acc, val) => acc + val, 0);
      result.push(sum / period);
    }
  }
  
  return result;
}

/**
 * Calculate Exponential Moving Average (EMA)
 */
export function calculateEMA(data: number[], period: number): (number | null)[] {
  const result: (number | null)[] = [];
  const multiplier = 2 / (period + 1);
  
  for (let i = 0; i < data.length; i++) {
    if (i === 0) {
      result.push(data[i]);
    } else if (i < period - 1) {
      // Use SMA for the first few values
      const sum = data.slice(0, i + 1).reduce((acc, val) => acc + val, 0);
      result.push(sum / (i + 1));
    } else {
      const ema = (data[i] - (result[i - 1] || 0)) * multiplier + (result[i - 1] || 0);
      result.push(ema);
    }
  }
  
  return result;
}

/**
 * Calculate Relative Strength Index (RSI)
 */
export function calculateRSI(data: number[], period: number = 14): (number | null)[] {
  const result: (number | null)[] = [];
  const gains: number[] = [];
  const losses: number[] = [];
  
  // Calculate price changes
  for (let i = 1; i < data.length; i++) {
    const change = data[i] - data[i - 1];
    gains.push(change > 0 ? change : 0);
    losses.push(change < 0 ? Math.abs(change) : 0);
  }
  
  // Calculate RSI
  result.push(null); // First value is null since we need price change
  
  for (let i = 0; i < gains.length; i++) {
    if (i < period - 1) {
      result.push(null);
    } else {
      const avgGain = gains.slice(i - period + 1, i + 1).reduce((acc, val) => acc + val, 0) / period;
      const avgLoss = losses.slice(i - period + 1, i + 1).reduce((acc, val) => acc + val, 0) / period;
      
      if (avgLoss === 0) {
        result.push(100);
      } else {
        const rs = avgGain / avgLoss;
        const rsi = 100 - (100 / (1 + rs));
        result.push(rsi);
      }
    }
  }
  
  return result;
}

/**
 * Detect Support and Resistance Levels
 */
export function detectSupportResistance(
  data: CandlestickData[],
  lookback: number = 20,
  minTouches: number = 2
): SupportResistanceLevel[] {
  const levels: SupportResistanceLevel[] = [];
  const pricePoints: { price: number; type: 'high' | 'low'; index: number }[] = [];
  
  // Find local highs and lows
  for (let i = lookback; i < data.length - lookback; i++) {
    const current = data[i];
    const isLocalHigh = data.slice(i - lookback, i + lookback + 1)
      .every(candle => candle.high <= current.high);
    const isLocalLow = data.slice(i - lookback, i + lookback + 1)
      .every(candle => candle.low >= current.low);
    
    if (isLocalHigh) {
      pricePoints.push({ price: current.high, type: 'high', index: i });
    }
    if (isLocalLow) {
      pricePoints.push({ price: current.low, type: 'low', index: i });
    }
  }
  
  // Group similar price levels
  const tolerance = 0.002; // 0.2% tolerance
  const groupedLevels: { [key: string]: { prices: number[]; type: 'high' | 'low'; indices: number[] } } = {};
  
  pricePoints.forEach(point => {
    const key = Math.round(point.price / (point.price * tolerance)).toString();
    if (!groupedLevels[key]) {
      groupedLevels[key] = { prices: [], type: point.type, indices: [] };
    }
    groupedLevels[key].prices.push(point.price);
    groupedLevels[key].indices.push(point.index);
  });
  
  // Create support/resistance levels
  Object.values(groupedLevels).forEach(group => {
    if (group.prices.length >= minTouches) {
      const avgPrice = group.prices.reduce((sum, price) => sum + price, 0) / group.prices.length;
      levels.push({
        price: avgPrice,
        strength: group.prices.length,
        type: group.type === 'high' ? 'resistance' : 'support',
        touches: group.prices.length
      });
    }
  });
  
  // Sort by strength (number of touches)
  return levels.sort((a, b) => b.strength - a.strength);
}

/**
 * Calculate Bollinger Bands
 */
export function calculateBollingerBands(
  data: number[],
  period: number = 20,
  stdDev: number = 2
): { upper: (number | null)[]; middle: (number | null)[]; lower: (number | null)[] } {
  const sma = calculateSMA(data, period);
  const upper: (number | null)[] = [];
  const lower: (number | null)[] = [];
  
  for (let i = 0; i < data.length; i++) {
    if (i < period - 1 || sma[i] === null) {
      upper.push(null);
      lower.push(null);
    } else {
      const slice = data.slice(i - period + 1, i + 1);
      const mean = sma[i]!;
      const variance = slice.reduce((acc, val) => acc + Math.pow(val - mean, 2), 0) / period;
      const standardDeviation = Math.sqrt(variance);
      
      upper.push(mean + (standardDeviation * stdDev));
      lower.push(mean - (standardDeviation * stdDev));
    }
  }
  
  return { upper, middle: sma, lower };
}

/**
 * Calculate MACD (Moving Average Convergence Divergence)
 */
export function calculateMACD(
  data: number[],
  fastPeriod: number = 12,
  slowPeriod: number = 26,
  signalPeriod: number = 9
): { macd: (number | null)[]; signal: (number | null)[]; histogram: (number | null)[] } {
  const fastEMA = calculateEMA(data, fastPeriod);
  const slowEMA = calculateEMA(data, slowPeriod);
  const macd: (number | null)[] = [];
  
  // Calculate MACD line
  for (let i = 0; i < data.length; i++) {
    if (fastEMA[i] !== null && slowEMA[i] !== null) {
      macd.push(fastEMA[i]! - slowEMA[i]!);
    } else {
      macd.push(null);
    }
  }
  
  // Calculate signal line (EMA of MACD)
  const macdValues = macd.filter(val => val !== null) as number[];
  const signal = calculateEMA(macdValues, signalPeriod);
  
  // Pad signal array to match original length
  const paddedSignal: (number | null)[] = new Array(macd.length - macdValues.length).fill(null);
  paddedSignal.push(...signal);
  
  // Calculate histogram
  const histogram: (number | null)[] = [];
  for (let i = 0; i < macd.length; i++) {
    if (macd[i] !== null && paddedSignal[i] !== null) {
      histogram.push(macd[i]! - paddedSignal[i]!);
    } else {
      histogram.push(null);
    }
  }
  
  return { macd, signal: paddedSignal, histogram };
}

/**
 * Transform kline data to candlestick format with indicators
 */
export function transformKlineDataWithIndicators(
  klineData: KlineData,
  selectedTimeframe: { label: string; minutes: number }
): {
  chartData: CandlestickData[];
  indicators: {
    sma20: (number | null)[];
    sma50: (number | null)[];
    ema20: (number | null)[];
    rsi: (number | null)[];
    supportResistance: SupportResistanceLevel[];
    bollingerBands: { upper: (number | null)[]; middle: (number | null)[]; lower: (number | null)[] };
  };
} {
  console.log('🔧 transformKlineDataWithIndicators input:', {
    klineData,
    timeLength: klineData?.time?.length,
    openLength: klineData?.open?.length,
    closeLength: klineData?.close?.length,
    highLength: klineData?.high?.length,
    lowLength: klineData?.low?.length,
    volLength: klineData?.vol?.length,
    selectedTimeframe
  });

  const chartData: CandlestickData[] = klineData.time.map((timestamp, index) => {
    const open = klineData.open[index] || 0;
    const high = klineData.high[index] || 0;
    const low = klineData.low[index] || 0;
    const close = klineData.close[index] || 0;

    const candlestick = {
      timestamp,
      time: new Date(timestamp * 1000).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        ...(selectedTimeframe.minutes >= 1440 && {
          month: 'short',
          day: 'numeric'
        })
      }),
      open,
      high,
      low,
      close,
      volume: klineData.vol[index] || 0,
      amount: klineData.amount[index] || 0,
    };

    if (index < 3) { // Log first 3 data points for debugging
      console.log(`🔧 Candlestick ${index}:`, candlestick);
    }

    return candlestick;
  });

  console.log('🔧 Generated chartData:', {
    length: chartData.length,
    firstItem: chartData[0],
    lastItem: chartData[chartData.length - 1]
  });

  const closePrices = klineData.close;

  const indicators = {
    sma20: calculateSMA(closePrices, 20),
    sma50: calculateSMA(closePrices, 50),
    ema20: calculateEMA(closePrices, 20),
    rsi: calculateRSI(closePrices, 14),
    supportResistance: detectSupportResistance(chartData),
    bollingerBands: calculateBollingerBands(closePrices, 20, 2)
  };

  console.log('🔧 Final result:', { chartDataLength: chartData.length, indicators });
  return { chartData, indicators };
}
